// frontend/src/components/AGTFit/utils/ProFeaturesModal.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Typography,
  Box,
  useTheme,
  IconButton,
  CircularProgress,
  Container,
  Slide
} from '@mui/material';
import {
  BrainCircuit,
  Camera,
  Dumbbell,
  X,
  Sparkles,
  Apple,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import SwipeableViews from 'react-swipeable-views';
import agtfitProService from '../service/agtfitProService';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const FeatureCard = ({ icon, title, description }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  return (
    <Box
      sx={{
        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
        borderRadius: '18px',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        backdropFilter: 'blur(10px)',
        boxShadow: isDarkMode 
          ? '0 4px 24px rgba(0,0,0,0.2)' 
          : '0 4px 24px rgba(0,0,0,0.05)',
        border: isDarkMode ? 'none' : '1px solid rgba(0,0,0,0.1)',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
        },
        height: '100%',
        minHeight: '260px',
        margin: '0 auto',
        maxWidth: '400px',
        width: '100%'
      }}
    >
      <Box
        sx={{
          backgroundColor: 'rgba(255, 125, 0, 0.1)',
          borderRadius: '16px',
          padding: '14px',
          marginBottom: '16px'
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: '600', mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
  );
};

const ProFeaturesModal = ({ open, onClose }) => {
  const theme = useTheme();
  const [loading, setLoading] = React.useState(false);
  const [activeStep, setActiveStep] = useState(0);
  // const fullScreen = useMediaQuery(theme.breakpoints.down('md')); // Unused variable
  const isDarkMode = theme.palette.mode === 'dark';

  const handleSubscribe = async () => {
    try {
      setLoading(true);
      const { url } = await agtfitProService.createCheckoutSession();
      window.location.href = url;
    } catch (error) {
      console.error('Error iniciando proceso de pago:', error);
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <BrainCircuit size={32} color="#FF7D00" />,
      title: 'Modelo de IA avanzado',
      description: 'Accede a AGT Fit Pro, un modelo más inteligente y capaz para recomendaciones personalizadas'
    },
    {
      icon: <Camera size={32} color="#FF7D00" />,
      title: 'Análisis de imágenes',
      description: 'Toma fotos de alimentos para análisis nutricional automático y preciso'
    },
    {
      icon: <Apple size={32} color="#FF7D00" />,
      title: 'Reconocimiento nutricional',
      description: 'Extrae datos de tablas nutricionales automáticamente con un simple escaneo'
    },
    {
      icon: <Dumbbell size={32} color="#FF7D00" />,
      title: 'Sin límites de mensajes',
      description: 'Conversa ilimitadamente con tu asistente de fitness personalizado'
    }
  ];
  
  const handleNext = () => {
    setActiveStep((prevActiveStep) => (prevActiveStep + 1) % features.length);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => (prevActiveStep - 1 + features.length) % features.length);
  };

  const handleStepChange = (step) => {
    setActiveStep(step);
  };

  // Definir colores basados en el tema
  const backgroundGradient = isDarkMode 
    ? 'linear-gradient(120deg, #000000, #1A1A1A)'
    : 'linear-gradient(120deg, #F5F5F5, #FFFFFF)';
  
  const textColor = isDarkMode ? 'white' : 'black';
  const secondaryTextColor = isDarkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)';
  const fadedTextColor = isDarkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)';
  
  const iconButtonBg = isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)';
  const iconButtonHoverBg = isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)';
  
  const priceBoxBg = isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)';
  const priceBoxShadow = isDarkMode 
    ? '0 8px 32px rgba(0,0,0,0.2)' 
    : '0 8px 32px rgba(0,0,0,0.08)';

  // El gradiente para textos especiales y botones se mantiene en ambos temas
  const specialGradient = 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)';
  const buttonHoverGradient = 'linear-gradient(90deg, #FF8000 0%, #FF5500 100%)';

  return (
    <Dialog
      fullScreen={true}
      open={open}
      onClose={loading ? null : onClose}
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          backgroundImage: backgroundGradient,
          overflowY: 'auto'
        }
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          padding: '24px',
          zIndex: 10
        }}
      >
        <IconButton
          onClick={onClose}
          disabled={loading}
          sx={{
            backgroundColor: iconButtonBg,
            backdropFilter: 'blur(8px)',
            '&:hover': {
              backgroundColor: iconButtonHoverBg,
            },
            color: textColor,
            padding: '8px'
          }}
        >
          <X size={24} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, height: '100%' }}>
        <Container maxWidth="md" sx={{ py: 4, height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              minHeight: '100%',
              py: 4
            }}
          >
            {/* Header - Logo & Título */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 6,
                pt: { xs: 4, sm: 8 }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Sparkles size={24} color="#FF9800" />
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    background: specialGradient,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  AGT Fit Pro
                </Typography>
              </Box>
              <Typography
                variant="h5"
                align="center"
                sx={{
                  fontWeight: 300,
                  color: textColor,
                  maxWidth: '600px',
                  px: 2
                }}
              >
                Planes personalizados y herramientas avanzadas para alcanzar tus metas
              </Typography>
            </Box>

            {/* Carrusel de características */}
            <Box sx={{ 
              maxWidth: 500, 
              width: '100%',
              margin: '0 auto',
              mb: 3,
              position: 'relative',
              minHeight: '300px'
            }}>
              <SwipeableViews
                axis={theme.direction === 'rtl' ? 'x-reverse' : 'x'}
                index={activeStep}
                onChangeIndex={handleStepChange}
                enableMouseEvents
              >
                {features.map((feature, index) => (
                  <div key={index} style={{ padding: '0 8px' }}>
                    <FeatureCard
                      icon={feature.icon}
                      title={feature.title}
                      description={feature.description}
                    />
                  </div>
                ))}
              </SwipeableViews>
              
              {/* Flechas de navegación */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '100%',
                zIndex: 2,
                px: 1
              }}>
                <IconButton 
                  onClick={handleBack}
                  sx={{ 
                    backgroundColor: iconButtonBg, 
                    color: textColor,
                    '&:hover': { backgroundColor: iconButtonHoverBg }
                  }}
                >
                  <ChevronLeft size={24} />
                </IconButton>
                <IconButton 
                  onClick={handleNext}
                  sx={{ 
                    backgroundColor: iconButtonBg, 
                    color: textColor,
                    '&:hover': { backgroundColor: iconButtonHoverBg }
                  }}
                >
                  <ChevronRight size={24} />
                </IconButton>
              </Box>
            </Box>
            
            {/* Puntos indicadores del carrusel */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              mb: 6 
            }}>
              {features.map((_, index) => (
                <Box
                  key={index}
                  onClick={() => handleStepChange(index)}
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    margin: '0 6px',
                    backgroundColor: index === activeStep ? '#FF7D00' : isDarkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s ease'
                  }}
                />
              ))}
            </Box>

            {/* Precio */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 6
              }}
            >
              <Box
                sx={{
                  background: priceBoxBg,
                  backdropFilter: 'blur(10px)',
                  borderRadius: '24px',
                  padding: '24px 48px',
                  textAlign: 'center',
                  boxShadow: priceBoxShadow,
                  border: isDarkMode ? 'none' : '1px solid rgba(0,0,0,0.1)'
                }}
              >
                <Typography variant="h6" sx={{ color: secondaryTextColor, mb: 1 }}>
                  Suscripción mensual
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    background: specialGradient,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1
                  }}
                >
                  $5 USD
                </Typography>
                <Typography variant="body2" sx={{ color: secondaryTextColor }}>
                  Cancela en cualquier momento
                </Typography>
              </Box>
            </Box>

            {/* Botón de suscripción */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                mb: { xs: 4, sm: 6 }
              }}
            >
              <Button
                variant="contained"
                onClick={handleSubscribe}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{
                  background: specialGradient,
                  color: 'white',
                  borderRadius: '16px',
                  padding: '14px 36px',
                  fontSize: '18px',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 8px 24px rgba(255, 125, 0, 0.3)',
                  '&:hover': {
                    background: buttonHoverGradient,
                    boxShadow: '0 8px 32px rgba(255, 125, 0, 0.5)',
                  },
                  minWidth: '220px'
                }}
              >
                {loading ? 'Procesando...' : 'Únete ahora'}
              </Button>
            </Box>

            {/* Nota legal */}
            <Typography
              variant="caption"
              align="center"
              sx={{ color: fadedTextColor, px: 2 }}
            >
              Al suscribirte, aceptas nuestros términos y política de privacidad.
              Puedes cancelar en cualquier momento.
            </Typography>
          </Box>
        </Container>
      </DialogContent>
    </Dialog>
  );
};

export default ProFeaturesModal;